import React, { useState, useEffect } from 'react';
import { useDropdownLevels } from '@/modules/levels/hooks/useLevels';
import { Icon } from '@/shared/icons'
import { Select } from '@/shared/components/ui'

interface UserPermissionsSectionProps {
  levelId?: string;
  onLevelChange?: (levelId: string | undefined) => void;
}

export const UserPermissionsSection: React.FC<UserPermissionsSectionProps> = ({
  levelId,
  onLevelChange,
}) => {
  const [selectedLevelId, setSelectedLevelId] = useState<string | undefined>(levelId);

  // Fetch levels for dropdown (public - no permissions required)
  const { data: activeLevels, isLoading: levelsLoading } = useDropdownLevels();

  // Update internal state when props change
  useEffect(() => {
    if (levelId !== undefined) {
      setSelectedLevelId(levelId);
    }
  }, [levelId]);

  // Handle level selection change
  const handleLevelChange = (value: string) => {
    const newLevelId = value || undefined;
    setSelectedLevelId(newLevelId);
    if (onLevelChange) {
      onLevelChange(newLevelId);
    }
  };

  // Get selected level details
  const selectedLevel = activeLevels?.find(level => level.id === selectedLevelId);

  const levelOptions = [
    { value: '', label: 'Nenhum nível selecionado' },
    ...(activeLevels?.map((level) => ({ value: level.id, label: level.name })) ?? []),
  ];

  return (
    <div
      className="rounded-xl border overflow-hidden"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
    >
      {/* Section Header (.card-h) */}
      <div
        className="flex items-start gap-3 px-[18px] py-[15px] border-b"
        style={{ borderColor: 'var(--theme-card-border)' }}
      >
        <div
          className="w-[34px] h-[34px] rounded-[9px] flex items-center justify-center flex-shrink-0"
          style={{
            backgroundColor: 'var(--theme-primary-background)',
            color: 'var(--theme-primary)',
          }}
        >
          <Icon name="lock" className="w-5 h-5" />
        </div>
        <div>
          <h2 className="text-[15px] font-bold" style={{ color: 'var(--theme-card-text)' }}>
            Permissões de Acesso
          </h2>
          <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
            Configure o nível de acesso do usuário
          </p>
        </div>
      </div>

      <div className="space-y-6 p-[18px]">
        {/* Level Selection */}
        <div>
          <label
            className="flex items-center gap-2 text-sm font-medium mb-2"
            style={{ color: 'var(--theme-card-text)' }}
          >
            <Icon name="shield" className="w-4 h-4" />
            Nível de Acesso
          </label>
          <Select
            options={levelOptions}
            value={selectedLevelId || ''}
            onChange={handleLevelChange}
            disabled={levelsLoading}
            data-testid="users-form-level"
          />

          {/* Selected Level Details */}
          {selectedLevel && (
            <div
              className="mt-3 flex items-start gap-2.5 p-3 rounded-lg border"
              style={{
                backgroundColor: 'var(--theme-dropdown-hover)',
                borderColor: 'var(--theme-card-border)',
              }}
            >
              <Icon name="shield" className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--theme-primary)' }} />
              <div>
                <p className="text-xs font-medium mb-1" style={{ color: 'var(--theme-card-text)' }}>
                  {selectedLevel.name}
                </p>
                {selectedLevel.description && (
                  <p className="text-xs" style={{ color: 'var(--theme-dropdown-text-muted)' }}>
                    {selectedLevel.description}
                  </p>
                )}
              </div>
            </div>
          )}

          <p className="text-xs mt-2 flex items-center gap-2" style={{ color: 'var(--theme-muted-text)' }}>
            <Icon name="info" className="w-3.5 h-3.5 flex-shrink-0" />
            O usuário herdará todas as permissões deste nível
          </p>
        </div>
      </div>
    </div>
  );
};

UserPermissionsSection.displayName = 'UserPermissionsSection';
